/* eslint-disable import/order */
import { PropsWithChildren, useCallback, useEffect } from 'react';

import { useDevice } from 'scbk-ui-web';

import type { WebviewType } from 'scbk-services';
import {
    Channel,
    EventType,
    initLocalStorage,
    Languages,
    Markets,
    registerLegacyFunctions,
    registerNativeBridgeReceiver,
    useAppBridge,
    useAppContext,
    useDeviceBridge,
    useEnvContext,
    useEventBusOn,
    useLocale,
    usePushBridge,
    useQuery,
    useRestContext,
    useWebviewContext,
} from 'scbk-services';
import { useAppsFlyer, usePopup } from 'scbk-shared-module';

import CloseAppPopup from '../popups/CloseAppPopup';
import { useAppInitContext } from '../providers/AppInitProvider';
import { NativeBridgeSubscriber } from '../subscribers';
import { NavigationBarController } from '../subscribers/NavigationBarController';

/**
 * 모바일뱅킹 앱 초기화 처리 컴포넌트
 * @param param0
 * @returns
 */
const MobileBankingInitializer: React.FC<PropsWithChildren> = ({ children }) => {
    useMobileBankingInitializer();

    //***************************************
    // 로그아웃 팝업 처리
    //***************************************
    const logoutPopup = usePopup('logoutPopup');
    useEventBusOn({
        eventType: EventType.OpenLogoutPopup,
        listener: () => {
            logoutPopup.open();
        },
    });

    return (
        <>
            {/* <VirtualKeypadProvider> */}
            {/* 하단 네비게이션바 제어 컴포넌트 */}
            <NavigationBarController />
            {/* 네이티브 브릿지 */}
            <NativeBridgeSubscriber />
            {children}
            {/* </VirtualKeypadProvider> */}
            {/* 로그아웃 팝업 */}
            {logoutPopup.isOpen && <CloseAppPopup {...logoutPopup} />}
        </>
    );
};

/**
 * 모바일뱅킹 앱 초기화 처리 hook
 */
const useMobileBankingInitializer = () => {
    const { getPreDefinedLocale, setLocale } = useLocale();
    const { getPreDefinedMarket } = useEnvContext();

    const { initialized, setInitialized } = useAppInitContext();
    const { setContext: setAppContext, updateContext: updateAppContext } = useAppContext();
    const { setContext: setRestContext, updateContext: updateRestContext } = useRestContext();
    const webviewContext = useWebviewContext();

    const { toggleLargeText, setThemeMode } = useDevice();

    const { doNGetDeviceStorage, doNGetLocale } = useAppBridge();
    const { doNReqIPInsideEXData, doNGetDeviceTheme } = useDeviceBridge();
    const { doNGetWebviewStorageKeys } = useAppBridge();
    const { doNGetPushService } = usePushBridge();
    const { init: initAppsFlyer } = useAppsFlyer();

    const { query } = useQuery();

    const defaultMarket = getPreDefinedMarket(import.meta.env) || Markets.KR;
    const defaultLocale = getPreDefinedLocale();

    /**
     * AppsFlyer 스크립트 import
     */
    initAppsFlyer();

    /**
     * 네이티브 웹뷰스토리지에 저장된 데이터를 localStorage와 동기화 처리를 수행한다.
     */
    const syncToLocalStorage = useCallback(async () => {
        // BIZ_DATA 삭제 방어로직
        initLocalStorage();

        const values = await doNGetWebviewStorageKeys();
        if (!values || values.length) {
            return;
        }

        console.log('### syncToLocalStorage keys ', values);
    }, [initLocalStorage, doNGetWebviewStorageKeys]);

    /**
     * IPINSIDE 정보 초기화 수행
     */
    const initIPInside = useCallback(async () => {
        //TODO 테스트 데이터
        const ipinsideInfo = await doNReqIPInsideEXData();
        //const ipinsideInfo = { natip: '', wdata: '', mac: '', mduinfo: '' };
        if (!ipinsideInfo) {
            return;
        }
        const { natip = '000.000.000.000', wdata = '', mac = '', mduinfo = '' } = ipinsideInfo;
        const ipinside = {
            natip,
            wdata,
            mac,
            mduinfo,
        };
        updateAppContext({ ipinside });
        updateRestContext({ ...ipinside });
    }, [doNReqIPInsideEXData, updateAppContext]);

    /**
     * user-agent 문자열에서 디바이스정보를 획득한다.
     * @returns 스마트뱅킹 user-agent 문자열에서 획득한 디바이스정보 object
     */
    const getDeviceInfoFromUserAgent = useCallback(():
        | {
              osType: string;
              uuid: string;
              deviceType: string;
              osVersion: string;
              osVersionSdk: string;
              deviceName: string;
              appVersion: string;
              simSerial: string;
              deviceCompany: string;
              androidId: string;
          }
        | undefined => {
        const userAgent = navigator.userAgent;
        if (!userAgent) {
            return;
        }

        const match = userAgent.match(/MA30\s*\(((?:[^()]|\([^()]*\))*)\)/);
        const appInfo = match ? match[1] : null;
        if (appInfo == null) {
            return;
        }

        const arr: string[] = appInfo.split(';');
        if (!arr || arr.length < 9) {
            return;
        }

        return {
            osType: arr[0].trim(),
            uuid: arr[1].trim(),
            deviceType: arr[2].trim(),
            osVersion: arr[3].trim(),
            osVersionSdk: arr[4].trim(),
            deviceName: arr[5].trim(),
            appVersion: arr[6].trim(),
            simSerial: arr[8].trim(),
            deviceCompany: arr[9].trim(),
            androidId: arr[10].trim(),
        };
    }, [navigator.userAgent]);

    /**
     * 큰글씨모드 초기화 처리
     */
    const initBigFontMode = useCallback(async () => {
        const bigFontMode = await doNGetDeviceStorage('MSC', 'BigFontYn');
        updateAppContext({ bigFontMode: bigFontMode === 'Y' ? 'Y' : 'N' });
    }, [doNGetDeviceStorage, updateAppContext]);

    /**
     * 고령화 모드 처리
     */
    const initSeniorMode = useCallback(async () => {
        const seniorMode = await doNGetDeviceStorage('CMM', 'SeniorMode');
        console.log('### init seniorMode : ', seniorMode);
        updateAppContext({ seniorMode: seniorMode === 'Y' ? 'Y' : 'N', isChangedSeniorMode: 'N' });
        toggleLargeText(seniorMode === 'Y');
    }, [doNGetDeviceStorage, updateAppContext]);

    /**
     * 다크모드 초기화
     */
    const initDarkMode = useCallback(
        async (osType: string, osVersion: string, appVersion: string) => {
            const osVersionArr = osVersion.split('.');
            const value = await doNGetDeviceStorage('CMM', 'DarkMode');
            let darkModeYn: 'Y' | 'N' = (value as 'Y' | 'N') ?? 'N';
            if (value === null || value === '') {
                if (
                    (osType === 'android' &&
                        appVersion >= '1.5.0' &&
                        Number(osVersionArr[0]) >= 10) ||
                    (osType === 'ios' && appVersion >= '1.4.0' && Number(osVersionArr[0]) >= 13)
                ) {
                    //
                    const result = await doNGetDeviceTheme();
                    darkModeYn = result === 'D' ? 'Y' : 'N';
                }
            }

            updateAppContext({ darkMode: darkModeYn });
            setThemeMode(darkModeYn === 'Y' ? 'dark' : 'light');
        },
        [doNGetDeviceStorage, updateAppContext],
    );

    /**
     * 언어(로케일)정보 초기화
     */
    const initLocale = useCallback(async () => {
        const nativeLocale = await doNGetLocale();
        const locale = (nativeLocale ?? defaultLocale ?? Languages.KO) as Languages;

        setLocale(locale);
    }, [doNGetLocale, defaultLocale, setLocale, updateAppContext, updateRestContext]);

    /**
     * 디바이스정보를 초기화한다.
     */
    const initDeviceInfo = useCallback(async () => {
        //app agent header 정보 설정
        const deviceInfo = getDeviceInfoFromUserAgent();

        //PUSH서비스 관련 토큰
        const deviceToken = await doNGetPushService();
        const device = {
            ...(deviceInfo ?? deviceInfo),
            deviceToken,
            devicePhone: '00000000000',
        };

        const os = {
            osType: (deviceInfo?.osType ?? '').toLowerCase(),
            osVersion: deviceInfo?.osVersion ?? '',
            osVersionSdk: deviceInfo?.osVersionSdk ?? '',
        };

        const app = {
            appVersion: deviceInfo?.appVersion ?? '',
        };

        updateAppContext({ device, os, app });
        updateRestContext({ ...device });

        await Promise.all([
            //고령화모드
            initSeniorMode(),
            //다크모드
            initDarkMode(os.osType, os.osVersion, app.appVersion),
            //큰글씨모드
            //initBigFontMode(),
            //IPINSIDE 정보 초기화
            initIPInside(),
        ]);
    }, [
        initSeniorMode,
        initDarkMode,
        initBigFontMode,
        initIPInside,
        doNGetPushService,
        getDeviceInfoFromUserAgent,
        updateAppContext,
    ]);

    /**
     * 초기화 수행
     */
    useEffect(() => {
        //이미 초기화가 되었다면 return;
        if (initialized) {
            return;
        }

        console.log('# useMobileBankingInitializer 초기화 처리 시작...');

        //웹뷰타입이 main이거나 없는경우 webview stack을 클리어 한다.
        const webviewType = query['webviewType'] as WebviewType;
        const webviewId = query['webviewId'] ?? '';

        console.log('# useMobileBankingInitializer webviewId, webviewType', {
            webviewId,
            webviewType,
        });

        //네이티브 브릿지 콜백 함수 window 등록
        registerNativeBridgeReceiver();

        //legacy 함수 window 등록
        registerLegacyFunctions();

        syncToLocalStorage();

        //디바이스 정보 초기화
        initDeviceInfo();

        //rest api context update
        setRestContext({
            country: defaultMarket.toUpperCase(),
            channel: Channel.MB,
        });

        setAppContext({
            country: defaultMarket.toUpperCase(),
            channel: Channel.MB,
            webviewType,
            webviewId,
            initialized: true,
        });

        initLocale();

        //************************************************
        //웹뷰컨텍스트 정보
        //************************************************
        webviewContext.activeWebview({ webviewId, webviewType });

        //초기화 완료
        setInitialized(true);

        //초기화 완료 이벤트
        //emit(EventType.ScCoreReady);

        console.log('# useMobileBankingInitializer 초기화 처리 완료...');
    }, [initialized, setInitialized]);

    return {
        initialized,
    };
};

export { MobileBankingInitializer };
